import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { TableColumn } from '../components/common/MobileCardList';
import DebouncedSearchInput from '../components/common/DebouncedSearchInput';
import { useQuery } from '@tanstack/react-query';
import { grnService } from '../services/grnService';

export default function GrnList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(true);
  const [page, setPage] = useState(1);

  // Fetch GRNs
  const { data: grnData, isLoading, isError, error } = useQuery({
    queryKey: ['grn', 'list', { page, search: searchTerm }],
    queryFn: async () => {
      const response = await grnService.getGrnList({ page, limit: 20, search: searchTerm });
      return response;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query);
    setPage(1);
  }, []);

  // GRN list
  const grns = useMemo(() => {
    return grnData?.items || [];
  }, [grnData]);

  // Handle view GRN
  const handleViewGrn = (grn: any) => {
    navigate(`/grn/${grn.id}`);
  };

  // Shared by the desktop table and the mobile card list
  const grnColumns: TableColumn[] = [
    {
      key: 'grnNumber',
      label: 'GRN Number',
      width: '150px',
      render: (value) => (
        <div className="font-medium text-gray-900" title={value}>
          {value}
        </div>
      )
    },
    {
      key: 'client',
      label: 'Client Name',
      width: '200px',
      render: (value) => (
        <div className="truncate" title={value?.companyName}>
          {value?.companyName || '-'}
        </div>
      )
    },
    {
      key: 'gateEntry',
      label: 'Entry Number',
      width: '150px',
      render: (value) => (
        <div className="text-sm text-gray-900" title={value?.entryNumber}>
          {value?.entryNumber || '-'}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (value) => (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap
          ${value === 'draft' ? 'bg-yellow-100 text-yellow-800' :
            value === 'confirmed' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'}`}
        >
          {value === 'draft' ? 'Draft' : value === 'confirmed' ? 'Confirmed' : value}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Created At',
      width: '150px',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value ? new Date(value).toLocaleString() : '-'}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (_, grn) => (
        <button
          onClick={() => handleViewGrn(grn)}
          className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 flex items-center"
        >
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          View
        </button>
      )
    }
  ];

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading GRNs...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (isError) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-red-600 font-medium">Failed to load GRNs</p>
            <p className="text-gray-600 text-sm mt-2">{(error as any)?.message}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900">GRN List</h2>
          <p className="text-gray-600 mt-1">View all Goods Receipt Notes</p>
        </div>

        {/* GRN Table */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              All GRNs
              {grns.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({grns.length} {grns.length === 1 ? 'record' : 'records'})
                </span>
              )}
            </h3>
          </div>

          {/* Search */}
          <div className="mb-4">
            {searchOpen ? (
              <DebouncedSearchInput
                placeholder="Search by GRN number, client, or entry number..."
                onSearch={handleSearch}
                debounceMs={300}
                onClose={() => { setSearchOpen(false); handleSearch(''); }}
              />
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
            )}
          </div>

          <ResponsiveTable
            columns={grnColumns}
            data={grns}
            isLoading={false}
            height="600px"
            emptyMessage="No GRNs found."
          />
        </div>
      </div>
    </Layout>
  );
}
